/**
 * Familienkonto — Kontenliste (aus PflegePage abgeleitet)
 *
 * Spalten: Konto/Name · betreute Person(en) · #Personen · Pflegegrad(e) ·
 * Status/Phase. Serverseitige Suche, Filter (Status, Pflegegrad, Phase,
 * Zugewiesen-an, Abwesenheit, BK-Suche) + Pagination (page/per_page), analog
 * zur PB-Liste. Button „Neues Familienkonto".
 *
 * Filter-Semantik: Weil ein Familienkonto mehrere Pflegeempfänger bündeln kann,
 * trifft ein Filter (z.B. Pflegegrad=3) auf ein Konto dann zu, wenn MINDESTENS
 * EINER seiner Pflegeempfänger das Kriterium erfüllt. Das Konto bleibt als
 * Gruppe sichtbar (nicht einzelne PB-Zeilen). Sortierung erfolgt auf Kontoebene.
 */
import { readAssigneeFilter, writeAssigneeFilter } from '../../../lib/assigneeFilterPref';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Card, Button } from '../../../components/ui';
import { Icon } from '../../../components/icons';
import { getThemeColors, DENSITY_VALUES } from '../../../lib/theme';
import { SearchableSelect } from '../../../components/SearchableSelect';
import { AdminMultiFilter } from '../../../components/AdminMultiFilter';
import { CareIconLegend } from '../../../components/care/CareParamIcons';
import { ColumnPicker, type ColumnDef } from '../../../components/ColumnPicker';
import { useColumnPreferences } from '../../../lib/useColumnPreferences';
import { useAuth } from '../../../contexts/AuthContext';
import { v1Admins, type V1Admin } from '../../../lib/v1';
import { PB_PHASE_KEYS, PB_PHASE_COUNT } from '../../../components/PbPhaseStepper';
import type { Density } from '../../../types';
import {
  v2FamilyAccounts, type FamilyAccount, type V2Meta,
} from '../../../lib/familyAccounts';
import { FamilienkontoDrawer, type FamilienkontoDrawerTab } from './FamilienkontoDrawer';
import { renderAccountCell } from './FamilienkontoListHelpers';
import { buildPath } from '../../../lib/route';
import { AppLink, rowLinkProps, isNewTabClick } from '../../../lib/openLink';

const PER_PAGE = 50;

// Spaltendefinition für den ColumnPicker. `id` = Spalten-ID (matcht `headers`
// unten + die Zell-Renderreihenfolge), `labelKey` wird zur Laufzeit übersetzt.
// Reihenfolge = visuelle Default-Reihenfolge; der Picker bewahrt sie.
const FK_COLUMNS: (ColumnDef & { labelKey: string })[] = [
  { id: 'account',       labelKey: 'pflege.familienkonto.list.header.account',         label: 'Konto', required: true },
  { id: 'care_recip',    labelKey: 'pflege.familienkonto.list.header.care_recipients', label: 'Betreute Person(en)' },
  { id: 'members_count', labelKey: 'pflege.familienkonto.list.header.members_count',   label: 'Personen' },
  { id: 'pflegegrad',    labelKey: 'pflege.familienkonto.list.header.pflegegrad',      label: 'Pflegegrad(e)' },
  // Pflegeparameter-Icons (Kacheln-PDF) — Einschränkungen/Bedürfnisse auf einen Blick
  { id: 'care_icons',    labelKey: 'pflege.list.header.care_icons',                    label: 'Pflegeparameter' },
  // Abwesenheit im laufenden Monat inkl. Grund (z. B. Krankenhaus)
  { id: 'absence',       labelKey: 'pflege.list.header.absence',                       label: 'Abwesenheit' },
  // Batch-13 Paket C Nacharbeit — Status-Spalte (PhaseCell) entfernt, ersetzt
  // durch Betreuungskraft + Ansprechpartner des primären Pflegeempfängers.
  { id: 'caregiver',     labelKey: 'pflege.list.header.caregiver',                     label: 'Betreuungskraft' },
  { id: 'contact_name',  labelKey: 'pflege.list.header.contact_name',                  label: 'Ansprechpartner Name' },
  // SGB V aktiv in den Profildaten eines Pflegeempfängers
  { id: 'sgb5',          labelKey: 'pflege.list.header.sgb5',                          label: 'SGB5' },
];

const FK_DEFAULT_COLS = FK_COLUMNS.map(c => c.id);

// Status-Konto-Filter analog zu PflegePage: UI-Status → konto-Werte (whereIn).
const STATUS_KONTO_FILTER: Record<string, number[]> = {
  'Aktiv':      [4],
  'Ausstehend': [2],
  'Beendet':    [0, 1, 3],
};

// Sortierbare Spalten + serverseitiger Feldname (Batch-13 Paket C Nacharbeit —
// vollständige Sort-Whitelist analog PbListController, siehe
// FamilyAccountController::derivedSorts()). `care_icons` bleibt unsortierbar
// (Icon-Liste, kein Einzelwert), analog PflegePage.
const SORT_FIELDS: Record<string, string> = {
  account:       'name',
  care_recip:    'care_recip',
  members_count: 'members_count',
  pflegegrad:    'pflegegrad',
  absence:       'absence',
  caregiver:     'bk_name',
  contact_name:  'contact_name',
  since:         'created_at',
};

interface Props {
  dark: boolean;
  density: Density;
  onOpenAccount: (id: number) => void;
  onNewAccount: () => void;
}

export function FamilienkontoListPage({ dark, density, onOpenAccount, onNewAccount }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const d = DENSITY_VALUES[density];
  const { user } = useAuth();

  // ── Spaltenauswahl (per-Admin, localStorage) ─────────────────────────────
  const [visibleCols, setVisibleCols] = useColumnPreferences(
    'familienkonto.table',
    user?.id ?? null,
    FK_DEFAULT_COLS,
  );

  // ── Listendaten ─────────────────────────────────────────────────────────
  const [items, setItems] = useState<FamilyAccount[]>([]);
  const [meta, setMeta] = useState<V2Meta | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Filter-State ────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  /** Debounced Hauptsuche (Name / Personenname / ID). */
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('Alle');
  const [pflegegradFilter, setPflegegradFilter] = useState<number | 'Alle'>('Alle');
  const [phaseFilter, setPhaseFilter] = useState<number | 'Alle'>('Alle');
  // Mitarbeiter-Mehrfachauswahl wie in der Anfragen-Liste; leer = alle.
  // Auswahl pro Admin merken (Google-Aufgabe Nr. 22, Vertretung bei Abwesenheit).
  const [assignedToFilter, setAssignedToFilter] = useState<number[]>(
    () => (user?.id != null ? readAssigneeFilter('pflege', user.id) ?? [] : []),
  );
  const [assigneeRestoredFor, setAssigneeRestoredFor] = useState<number | null>(user?.id ?? null);
  useEffect(() => {
    if (user?.id == null || assigneeRestoredFor === user.id) return;
    setAssignedToFilter(readAssigneeFilter('pflege', user.id) ?? []);
    setAssigneeRestoredFor(user.id);
  }, [user?.id, assigneeRestoredFor]);
  const changeAssignedTo = (ids: number[]) => {
    setAssignedToFilter(ids);
    writeAssigneeFilter('pflege', user?.id ?? null, ids);
  };
  // 'active' = laeuft gerade · 'month' = irgendwann im laufenden Monat · 'none' = anwesend
  const [absenceFilter, setAbsenceFilter] = useState<'Alle' | 'active' | 'month' | 'none'>('Alle');
  const [bkSearch, setBkSearch] = useState('');
  /** Debounced BK-Suche. */
  const [bkQuery, setBkQuery] = useState('');

  // ── Sortierung ──────────────────────────────────────────────────────────
  // Kein Default-Sort: erster Klick → asc, zweiter auf gleiche Spalte → desc.
  // Leerer string = kein aktiver Indikator; Backend-Default (id asc) bleibt.
  const [sortCol, setSortCol] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // ── Vorschau-Drawer ──────────────────────────────────────────────────────
  const [drawerAccount, setDrawerAccount] = useState<FamilyAccount | null>(null);
  const [drawerTab, setDrawerTab] = useState<FamilienkontoDrawerTab>('aufgaben');

  // ── Admin-Liste für "Zugewiesen an" ─────────────────────────────────────
  const [admins, setAdmins] = useState<V1Admin[]>([]);

  // ── Debounce Suchen ──────────────────────────────────────────────────────
  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    const id = setTimeout(() => setBkQuery(bkSearch.trim()), 350);
    return () => clearTimeout(id);
  }, [bkSearch]);

  // ── Admins einmalig laden ────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    v1Admins.list()
      .then(list => { if (alive) setAdmins(list); })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, []);

  // ── Aktuelle Filter-Parameter (für Seite-1-Reload + loadMore) ───────────
  const currentParams = useMemo(() => {
    const p: Parameters<typeof v2FamilyAccounts.list>[0] = {
      page: 1,
      per_page: PER_PAGE,
      // Anfragen erscheinen erst hier, wenn sie die letzte Phase erreicht
      // haben — vorher leben sie auf der Anfragen-Seite.
      only_final_phase: true,
      // Batch-13 Paket C Nacharbeit — /pflege = "Kunde"-Definition (letzte
      // aktive Stage UND bestätigte Betreuung), strenger als only_final_phase
      // allein. FamilienkontoListPage wird ausschließlich für /pflege
      // gerendert (siehe App.tsx), daher fest verdrahtet statt Prop.
      scope: 'kunden',
    };
    if (query) p.search = query;
    const konto = STATUS_KONTO_FILTER[statusFilter];
    if (konto) p.konto = konto;
    if (pflegegradFilter !== 'Alle') p.pflegegrad = pflegegradFilter as number;
    if (phaseFilter !== 'Alle') p.phase = phaseFilter as number;
    if (assignedToFilter.length) p.assigned_to = assignedToFilter;
    if (bkQuery) p.bk_name = bkQuery;
    if (absenceFilter !== 'Alle') p.absence = absenceFilter;
    const sf = SORT_FIELDS[sortCol];
    if (sf) { p.order_by = sf; p.order = sortDir; }
    return p;
  }, [query, statusFilter, pflegegradFilter, phaseFilter, assignedToFilter, bkQuery, absenceFilter, sortCol, sortDir]);

  // ── Seite 1 neu laden ────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await v2FamilyAccounts.list({ ...currentParams, page: 1 });
        if (!alive) return;
        setItems(res.data);
        setMeta(res.meta);
      } catch (e) {
        if (!alive) return;
        setError(e instanceof Error ? e.message : tr('pflege.familienkonto.list.error'));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [currentParams]);

  // ── "Mehr laden" ─────────────────────────────────────────────────────────
  const loadMore = async () => {
    if (loadingMore || !meta || meta.current_page >= meta.last_page) return;
    setLoadingMore(true);
    try {
      const res = await v2FamilyAccounts.list({
        ...currentParams,
        page: meta.current_page + 1,
      });
      setItems(prev => [...prev, ...res.data]);
      setMeta(res.meta);
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('pflege.familienkonto.list.error'));
    } finally {
      setLoadingMore(false);
    }
  };

  // ── Sortier-Toggle ────────────────────────────────────────────────────────
  const toggleSort = (col: string) => {
    if (!SORT_FIELDS[col]) return;
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const total = meta?.total ?? items.length;

  // ── Tabellen-Header-Konfiguration ─────────────────────────────────────────
  // Sortierbare Spalten (serverseitiges Feld via SORT_FIELDS).
  const SORTABLE = new Set(Object.keys(SORT_FIELDS));
  // Spalten mit zur Laufzeit übersetzten Labels (für Tabelle + ColumnPicker).
  const translatedColumns = FK_COLUMNS.map(c => ({ ...c, label: tr(c.labelKey) }));
  // Nur die aktuell sichtbaren Spalten (Reihenfolge = Definition).
  const cols = translatedColumns.filter(c => visibleCols.includes(c.id));
  const headers = cols.map(c => ({ id: c.id, label: c.label, sortable: SORTABLE.has(c.id) }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap, height: '100%', padding: 24 }}>
      {/* Zeilen-Aktionen werden nur bei Mouseover / Fokus der Zeile eingeblendet. */}
      <style>{`
        .fk-row .fk-row-actions { opacity: 0; transition: opacity .12s ease; pointer-events: none; }
        .fk-row:hover .fk-row-actions, .fk-row:focus-within .fk-row-actions { opacity: 1; pointer-events: auto; }
      `}</style>
      {error && (
        <Card dark={dark} style={{ padding: 12, borderColor: '#f04438' }}>
          <div style={{ fontSize: 13, color: '#b42318', fontWeight: 500 }}>{error}</div>
        </Card>
      )}

      {/* Toolbar — Filter-Zeile */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Hauptsuche */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <span style={{ position: 'absolute', left: 10 }}><Icon.Search c={t.placeholder} size={14} /></span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={tr('pflege.familienkonto.list.search_placeholder')}
            style={{ fontSize: 12, border: `1px solid ${t.border}`, borderRadius: 8, padding: '7px 12px 7px 32px', width: 260, background: t.inputBg, color: t.text, fontFamily: 'Outfit,sans-serif', outline: 'none' }}
          />
        </div>

        {/* Status */}
        <SearchableSelect
          dark={dark}
          ariaLabel={tr('pflege.list.header.status')}
          value={statusFilter}
          onChange={setStatusFilter}
          width={150}
          options={[
            { value: 'Alle',      label: tr('pflege.list.status.all') },
            { value: 'Aktiv',     label: tr('pflege.list.status.active') },
            { value: 'Ausstehend', label: tr('pflege.list.status.pending') },
            { value: 'Beendet',   label: tr('pflege.list.status.closed') },
          ]}
        />

        {/* Pflegegrad */}
        <SearchableSelect
          dark={dark}
          ariaLabel={tr('pflege.filter.pflegegrad')}
          value={pflegegradFilter === 'Alle' ? 'Alle' : String(pflegegradFilter)}
          onChange={v => setPflegegradFilter(v === 'Alle' ? 'Alle' : Number(v))}
          width={160}
          options={[
            { value: 'Alle', label: tr('pflege.filter.pflegegrad_all') },
            ...[1, 2, 3, 4, 5].map(pg => ({ value: String(pg), label: `PG ${pg}` })),
          ]}
        />

        {/* Zugewiesen an — Mehrfachauswahl (gemeinsame Komponente mit Anfragen) */}
        <AdminMultiFilter
          dark={dark}
          admins={admins}
          value={assignedToFilter}
          onChange={changeAssignedTo}
          currentAdminId={user?.id ?? null}
        />

        {/* Abwesenheit — serverseitig: absence=active/month/none via FamilyAccountController.
            active = laeuft exakt jetzt, month = irgendwann im laufenden Monat
            (auch bereits beendet oder noch bevorstehend), none = anwesend. */}
        <SearchableSelect
          dark={dark}
          ariaLabel={tr('pflege.list.header.absence')}
          value={absenceFilter}
          onChange={v => setAbsenceFilter(v as 'Alle' | 'active' | 'month' | 'none')}
          width={200}
          options={[
            { value: 'Alle',   label: tr('pflege.filter.absence_all') },
            { value: 'active', label: tr('pflege.filter.absence_active') },
            { value: 'month',  label: tr('pflege.filter.absence_month') },
            { value: 'none',   label: tr('pflege.filter.absence_none') },
          ]}
        />

        {/* BK-Suche */}
        <input
          aria-label={tr('pflege.list.header.caregiver')}
          value={bkSearch}
          onChange={e => setBkSearch(e.target.value)}
          placeholder={tr('pflege.filter.bk_placeholder')}
          style={{ fontSize: 12, border: `1px solid ${t.border}`, borderRadius: 8, padding: '7px 10px', width: 180, background: t.inputBg, color: t.text, fontFamily: 'Outfit,sans-serif', outline: 'none' }}
        />

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <ColumnPicker dark={dark} columns={translatedColumns} visible={visibleCols} onChange={setVisibleCols} />
          <Button variant="primary" size="sm" dark={dark} icon={<Icon.Plus />} onClick={onNewAccount}>
            {tr('pflege.familienkonto.list.new')}
          </Button>
        </div>
      </div>

      {/* Tabelle */}
      <Card dark={dark} style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ overflow: 'auto', flex: 1 }}>
          <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ position: 'sticky', top: 0, background: t.tableHeader, zIndex: 1 }}>
              <tr>
                {/* Aktionsspalte (Hover-Buttons) — Batch-12: ganz links */}
                <th
                  aria-label={tr('pflege.list.header.actions')}
                  style={{ padding: `${d.rowY}px ${d.cellX}px`, textAlign: 'left', fontWeight: 600, fontSize: 11, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${t.border}`, whiteSpace: 'nowrap', width: 1 }}
                />
                {headers.map(h => {
                  const active = h.sortable && sortCol === h.id;
                  return (
                    <th
                      key={h.id}
                      onClick={h.sortable ? () => toggleSort(h.id) : undefined}
                      style={{
                        padding: `${d.rowY}px ${d.cellX}px`,
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: 11,
                        color: active ? t.heading : t.muted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        borderBottom: `1px solid ${t.border}`,
                        whiteSpace: 'nowrap',
                        cursor: h.sortable ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                    >
                      <span>{h.label}</span>
                      {active && <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                      {/* A12-Regression (Staging 22.09.2026): Legende fehlte hier —
                          /pflege rendert diese Seite, nicht PflegePage. */}
                      {h.id === 'care_icons' && <CareIconLegend dark={dark} label={tr('pflege.list.care_icons_legend')} />}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr><td colSpan={cols.length + 1} style={{ padding: 20, color: t.muted, fontSize: 12, textAlign: 'center' }}>{tr('pflege.familienkonto.list.loading')}</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={cols.length + 1} style={{ padding: 20, color: t.muted, fontSize: 12, textAlign: 'center' }}>{tr('pflege.familienkonto.list.empty')}</td></tr>
              )}
              {items.map(acc => (
                <tr
                  key={acc.id}
                  className="fk-row"
                  {...rowLinkProps(buildPath('pflege', 'konto', String(acc.id)), () => { setDrawerAccount(acc); setDrawerTab('aufgaben'); })}
                  style={{ borderBottom: `1px solid ${t.borderSoft}`, cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = t.hover}
                  onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'}
                >
                  {/* Hover-Aktionen: ganz links, nur bei Mouseover / Fokus sichtbar (siehe .fk-row CSS). */}
                  <td style={{ padding: `${d.rowY}px ${d.cellX}px`, textAlign: 'left', whiteSpace: 'nowrap' }}>
                    <div className="fk-row-actions" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <AppLink
                        page="pflege"
                        sub="konto"
                        tab={String(acc.id)}
                        stopPropagation
                        title={tr('pflege.list.action.edit_row')}
                        aria-label={tr('pflege.list.action.edit_row')}
                        onClick={e => { if (!isNewTabClick(e)) { e.preventDefault(); onOpenAccount(acc.id); } }}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, border: `1px solid ${t.border}`, borderRadius: 7, background: t.inputBg, cursor: 'pointer', textDecoration: 'none' }}
                      >
                        <Icon.Edit c={t.muted} />
                      </AppLink>
                      <button
                        type="button"
                        title={tr('pflege.list.action.tasks_row')}
                        aria-label={tr('pflege.list.action.tasks_row')}
                        onClick={e => { e.stopPropagation(); setDrawerAccount(acc); setDrawerTab('aufgaben'); }}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, border: `1px solid ${t.border}`, borderRadius: 7, background: t.inputBg, cursor: 'pointer' }}
                      >
                        <Icon.File c={t.muted} />
                      </button>
                    </div>
                  </td>
                  {cols.map(col => (
                    <td key={col.id} style={{ padding: `${d.rowY}px ${d.cellX}px`, color: t.text }}>
                      {renderAccountCell(acc, col.id, dark, t)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Footer: Zähler + Mehr laden */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: t.muted }}>
          {tr('pflege.familienkonto.list.count', { shown: items.length, total })}
        </span>
        {meta && meta.current_page < meta.last_page && (
          <Button variant="secondary" size="sm" dark={dark} disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? tr('pflege.familienkonto.list.loading') : tr('pflege.familienkonto.list.load_more')}
          </Button>
        )}
      </div>

      {drawerAccount != null && (
        <FamilienkontoDrawer
          key={drawerAccount.id}
          account={drawerAccount}
          initialTab={drawerTab}
          dark={dark}
          admins={admins}
          onClose={() => setDrawerAccount(null)}
          onEdit={() => { onOpenAccount(drawerAccount.id); setDrawerAccount(null); }}
          onSaved={updated => setItems(prev => prev.map(a => a.id === updated.id ? updated : a))}
        />
      )}
    </div>
  );
}
